import React, { useState, useEffect, useCallback } from 'react';
import { 
  Building2, 
  MapPin, 
  Plus, 
  Trash2, 
  ChevronDown, 
  ChevronRight,
  Tag,
  UserCheck,
  Edit,
  ToggleLeft,
  ToggleRight,
  Eye,
  EyeOff,
  AlertCircle,
  Loader2
} from 'lucide-react';
import { useAdminAPI } from '../hooks/useAdminAPI';
import { AdminUser, AdminUserRole, Brand, Branch, PosUser, PosUserRole } from '../types/api';

interface BrandWithBranches extends Brand {
  branches: BranchWithUsers[];
}

interface BranchWithUsers extends Branch {
  users: PosUser[];
}

const GestionSection: React.FC = () => {
  const {
    getBrands,
    createBrand,
    deleteBrand,
    getBranches,
    createBranch,
    deleteBranch,
    getPosUsers,
    createPosUser,
    updatePosUser,
    deletePosUser,
    getAdminUsers,
    createAdminUser,
    updateAdminUser,
    deleteAdminUser
  } = useAdminAPI();

  const [brands, setBrands] = useState<BrandWithBranches[]>([]);
  const [expandedBrands, setExpandedBrands] = useState<Set<string>>(new Set());
  const [expandedBranches, setExpandedBranches] = useState<Set<string>>(new Set());
  
  // Loading and error states
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loadingBrands, setLoadingBrands] = useState<Set<string>>(new Set());
  const [loadingBranches, setLoadingBranches] = useState<Set<string>>(new Set());
  
  // Form states
  const [showBrandForm, setShowBrandForm] = useState(false);
  const [showBranchForm, setShowBranchForm] = useState(false);
  const [showUserForm, setShowUserForm] = useState(false);
  const [showEditUserForm, setShowEditUserForm] = useState(false);
  const [showAdminForm, setShowAdminForm] = useState(false);
  const [showEditAdminForm, setShowEditAdminForm] = useState(false);
  const [editingUser, setEditingUser] = useState<PosUser | null>(null);
  const [editingAdmin, setEditingAdmin] = useState<AdminUser | null>(null);
  const [editingAdminPassword, setEditingAdminPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showAdminPassword, setShowAdminPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [adminUsers, setAdminUsers] = useState<AdminUser[]>([]);
  
  // Form data
  const [brandForm, setBrandForm] = useState({ name: '', alias: '' });
  const [branchForm, setBranchForm] = useState({ 
    name: '', 
    alias: '', 
    address_street: '', 
    address_city: '',
    address_state: '',
    address_country: 'México',
    brandId: '' 
  });
  const [userForm, setUserForm] = useState<{ 
    full_name: string; 
    email: string; 
    password: string; 
    role: PosUserRole; 
    branchId: string; 
  }>({ 
    full_name: '', 
    email: '', 
    password: '', 
    role: 'cajero', 
    branchId: '' 
  });
  const [adminForm, setAdminForm] = useState<{
    name: string;
    email: string;
    password: string;
    role: AdminUserRole;
  }>({
    name: '',
    email: '',
    password: '',
    role: 'admin',
  });

  // Load brands on mount
  const loadBrands = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const brandsData = await getBrands();
      
      // Initialize brands with empty branches array
      const brandsWithBranches: BrandWithBranches[] = brandsData.map(brand => ({
        ...brand,
        branches: []
      }));
      
      setBrands(brandsWithBranches);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error al cargar las marcas';
      setError(errorMessage);
      console.error('Error loading brands:', err);
    } finally {
      setIsLoading(false);
    }
  }, [getBrands]);

  const loadAdminUsers = useCallback(async () => {
    try {
      const admins = await getAdminUsers();
      setAdminUsers(admins);
    } catch (err) {
      console.error('Error loading admin users:', err);
      setError(err instanceof Error ? err.message : 'Error al cargar administradores');
    }
  }, [getAdminUsers]);

  useEffect(() => {
    loadBrands();
    loadAdminUsers();
  }, [loadBrands, loadAdminUsers]);

  // Load branches when brand is expanded
  const loadBranchesForBrand = useCallback(async (brandId: string) => {
    if (loadingBranches.has(brandId)) return;
    
    try {
      setLoadingBranches(prev => new Set(prev).add(brandId));
      const branchesData = await getBranches(brandId);
      
      // Load users for each branch
      const branchesWithUsers: BranchWithUsers[] = await Promise.all(
        branchesData.map(async (branch) => {
          try {
            const users = await getPosUsers(branch.id);
            return { ...branch, users };
          } catch {
            return { ...branch, users: [] };
          }
        })
      );
      
      setBrands(prev => prev.map(brand => 
        brand.id === brandId 
          ? { ...brand, branches: branchesWithUsers }
          : brand
      ));
    } catch (err) {
      console.error('Error loading branches:', err);
      setError('Error al cargar las sucursales');
    } finally {
      setLoadingBranches(prev => {
        const newSet = new Set(prev);
        newSet.delete(brandId);
        return newSet;
      });
    }
  }, [getBranches, getPosUsers, loadingBranches]);

  // Toggle expansion
  const toggleBrandExpansion = async (brandId: string) => {
    const newExpanded = new Set(expandedBrands);
    const wasExpanded = newExpanded.has(brandId);
    
    if (wasExpanded) {
      newExpanded.delete(brandId);
    } else {
      newExpanded.add(brandId);
      // Load branches when expanding
      await loadBranchesForBrand(brandId);
    }
    
    setExpandedBrands(newExpanded);
  };

  const toggleBranchExpansion = (branchId: string) => {
    const newExpanded = new Set(expandedBranches);
    if (newExpanded.has(branchId)) {
      newExpanded.delete(branchId);
    } else {
      newExpanded.add(branchId);
    }
    setExpandedBranches(newExpanded);
  };

  // Brand management
  const addBrand = async () => {
    if (!brandForm.name || !brandForm.alias) {
      setError('Por favor completa todos los campos');
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);
      const newBrand = await createBrand({
        name: brandForm.name,
        alias: brandForm.alias
      });
      
      setBrands(prev => [...prev, { ...newBrand, branches: [] }]);
      setBrandForm({ name: '', alias: '' });
      setShowBrandForm(false);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error al crear la marca';
      setError(errorMessage);
      console.error('Error creating brand:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteBrand = async (brandId: string) => {
    if (!window.confirm('¿Estás seguro de que deseas eliminar esta marca? Esto eliminará todas sus sucursales y usuarios.')) {
      return;
    }

    try {
      setLoadingBrands(prev => new Set(prev).add(brandId));
      setError(null);
      console.log('🗑️ Eliminando marca:', brandId);
      await deleteBrand(brandId);
      console.log('✅ Marca eliminada exitosamente');
      // Recargar la lista completa para asegurar que el estado esté actualizado
      await loadBrands();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error al eliminar la marca';
      setError(errorMessage);
      console.error('❌ Error deleting brand:', err);
      alert(`Error al eliminar la marca: ${errorMessage}`);
    } finally {
      setLoadingBrands(prev => {
        const newSet = new Set(prev);
        newSet.delete(brandId);
        return newSet;
      });
    }
  };

  // Branch management
  const addBranch = async () => {
    if (!branchForm.name || !branchForm.alias || !branchForm.brandId) {
      setError('Por favor completa todos los campos requeridos');
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);
      const newBranch = await createBranch({
        brand_id: branchForm.brandId,
        name: branchForm.name,
        alias: branchForm.alias,
        address_street: branchForm.address_street || undefined,
        address_city: branchForm.address_city || undefined,
        address_state: branchForm.address_state || undefined,
        address_country: branchForm.address_country || undefined
      });
      
      setBrands(prev => prev.map(brand => 
        brand.id === branchForm.brandId 
          ? { ...brand, branches: [...brand.branches, { ...newBranch, users: [] }] }
          : brand
      ));
      
      setBranchForm({ 
        name: '', 
        alias: '', 
        address_street: '', 
        address_city: '',
        address_state: '',
        address_country: 'México',
        brandId: '' 
      });
      setShowBranchForm(false);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error al crear la sucursal';
      setError(errorMessage);
      console.error('Error creating branch:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteBranch = async (brandId: string, branchId: string) => {
    if (!window.confirm('¿Estás seguro de que deseas eliminar esta sucursal? Esto eliminará todos sus usuarios.')) {
      return;
    }

    try {
      setLoadingBranches(prev => new Set(prev).add(branchId));
      setError(null);
      console.log('🗑️ Eliminando sucursal:', branchId, 'de marca:', brandId);
      await deleteBranch(branchId);
      console.log('✅ Sucursal eliminada exitosamente');
      // Recargar la lista completa para asegurar que el estado esté actualizado
      await loadBrands();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error al eliminar la sucursal';
      setError(errorMessage);
      console.error('❌ Error deleting branch:', err);
      alert(`Error al eliminar la sucursal: ${errorMessage}`);
    } finally {
      setLoadingBranches(prev => {
        const newSet = new Set(prev);
        newSet.delete(branchId);
        return newSet;
      });
    }
  };

  // User management
  const addUser = async () => {
    if (!userForm.full_name || !userForm.email || !userForm.password || !userForm.branchId) {
      setError('Por favor completa todos los campos');
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);
      
      console.log('👤 Intentando crear usuario con email:', userForm.email, 'rol:', userForm.role);
      
      // Crear el usuario con el rol seleccionado
      let newUser: PosUser;
      try {
        newUser = await createPosUser({
          branch_id: userForm.branchId,
          full_name: userForm.full_name,
          email: userForm.email,
          password: userForm.password,
          role: userForm.role
        });
        console.log('✅ Usuario operador CENT creado exitosamente:', newUser.id);
      } catch (createErr: any) {
        // Si el error es que el email ya existe, verificar si es un operador_cent sin Admin
        if (
          createErr?.message?.includes('email ya está registrado') ||
          (createErr?.message?.includes('email') && createErr?.statusCode === 400)
        ) {
          console.log('⚠️ El email ya existe, verificando si necesita crear el Admin...');
          
          // Si es operador_cent, verificar si ya tiene un Admin asociado
          if (userForm.role === 'operador_cent') {
            try {
              const existingUsers = await getPosUsers(userForm.branchId);
              const operadorUser = existingUsers.find(u => 
                u.email.toLowerCase() === userForm.email.toLowerCase() && u.role === 'operador_cent'
              );
              
              const adminEmail = userForm.email.includes('@')
                ? userForm.email.replace('@', '+admin@')
                : `${userForm.email}+admin`;
              
              const adminExists = existingUsers.some(u => 
                u.email.toLowerCase() === adminEmail.toLowerCase()
              );
              
              if (operadorUser && !adminExists) {
                console.log('🔐 El operador CENT existe pero no tiene Admin, creando Admin...');
                // El operador CENT existe pero no tiene Admin, crear solo el Admin
                const adminUser = await createPosUser({
                  branch_id: userForm.branchId,
                  full_name: `${userForm.full_name} (Admin)`,
                  email: adminEmail,
                  password: userForm.password,
                  role: 'admin_tienda'
                });
                console.log('✅ Usuario Admin creado exitosamente:', adminUser.id);
                setError(null);
                setUserForm({ 
                  full_name: '', 
                  email: '', 
                  password: '', 
                  role: 'cajero', 
                  branchId: '' 
                });
                setShowUserForm(false);
                await loadBrands(); // Recargar la lista
                setIsSubmitting(false);
                return;
              }
            } catch (checkErr) {
              console.error('Error verificando usuarios existentes:', checkErr);
            }
          }
        }
        // Re-lanzar el error si no se pudo manejar
        throw createErr;
      }
      
      // Si el rol es "operador_cent", también crear un usuario Admin
      let adminUser: PosUser | null = null;
      if (userForm.role === 'operador_cent') {
        try {
          console.log('🔐 Creando usuario Admin para operador CENT...');
          
          // Crear un email único para el Admin usando el formato de alias (+admin)
          // Ejemplo: mikegoldstein100@gmail.com -> mikegoldstein100+admin@gmail.com
          const adminEmail = userForm.email.includes('@')
            ? userForm.email.replace('@', '+admin@')
            : `${userForm.email}+admin`;
          
          console.log('📧 Email del Admin (único):', adminEmail);
          
          adminUser = await createPosUser({
            branch_id: userForm.branchId,
            full_name: `${userForm.full_name} (Admin)`,
            email: adminEmail, // Email único con alias
            password: userForm.password, // Misma contraseña
            role: 'admin_tienda' // Rol de admin
          });
          console.log('✅ Usuario Admin POS creado exitosamente:', adminUser.id);
          console.log('⚠️ NOTA: Este es un usuario POS (admin_tienda), no un usuario Admin del sistema.');
          console.log('⚠️ Para hacer login en el dashboard admin, necesitas un usuario Admin del sistema creado por el backend.');
        } catch (adminErr) {
          console.error('⚠️ Error al crear usuario Admin:', adminErr);
          // No fallar la creación del usuario principal si falla la creación del admin
          const adminErrorMessage = adminErr instanceof Error ? adminErr.message : 'Error al crear el usuario Admin';
          setError(`Usuario operador CENT creado exitosamente, pero hubo un error al crear el usuario Admin: ${adminErrorMessage}`);
        }
      }
      
      // Actualizar el estado con ambos usuarios
      setBrands(prev => prev.map(brand => ({
        ...brand,
        branches: brand.branches.map(branch =>
          branch.id === userForm.branchId
            ? { 
                ...branch, 
                users: [
                  ...branch.users, 
                  newUser,
                  ...(adminUser ? [adminUser] : [])
                ]
              }
            : branch
        )
      })));
      
      // Mostrar mensaje informativo si se creó un operador CENT
      if (userForm.role === 'operador_cent' && adminUser) {
        setError(`✅ Usuarios creados exitosamente:
- Operador CENT: ${userForm.email}
- Admin POS: ${userForm.email.includes('@') ? userForm.email.replace('@', '+admin@') : `${userForm.email}+admin`}

⚠️ IMPORTANTE: Estos son usuarios POS, no usuarios Admin del sistema.
Para hacer login en el dashboard admin, el backend debe crear un usuario Admin del sistema.
Contacta al administrador del backend para crear tu usuario Admin del sistema.`);
      }
      
      setUserForm({ 
        full_name: '', 
        email: '', 
        password: '', 
        role: 'cajero', 
        branchId: '' 
      });
      setShowUserForm(false);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error al crear el usuario';
      setError(errorMessage);
      console.error('Error creating user:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteUser = async (brandId: string, branchId: string, userId: string) => {
    if (!window.confirm('¿Estás seguro de que deseas eliminar este usuario?')) {
      return;
    }

    try {
      setError(null);
      console.log('🗑️ Eliminando usuario:', userId, 'de sucursal:', branchId);
      await deletePosUser(userId);
      console.log('✅ Usuario eliminado exitosamente');
      // Recargar la lista completa para asegurar que el estado esté actualizado
      await loadBrands();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error al eliminar el usuario';
      setError(errorMessage);
      console.error('❌ Error deleting user:', err);
      alert(`Error al eliminar el usuario: ${errorMessage}`);
    }
  };

  const editUser = (user: PosUser) => {
    setEditingUser(user);
    setShowEditUserForm(true);
  };

  const updateUser = async () => {
    if (!editingUser) return;

    try {
      setIsSubmitting(true);
      setError(null);
      const updatedUser = await updatePosUser(editingUser.id, {
        full_name: editingUser.full_name,
        email: editingUser.email,
        role: editingUser.role,
        is_active: editingUser.is_active
      });
      
      setBrands(prev => prev.map(brand => ({
        ...brand,
        branches: brand.branches.map(branch =>
          branch.id === editingUser.branch_id
            ? {
                ...branch,
                users: branch.users.map(user =>
                  user.id === editingUser.id
                    ? updatedUser
                    : user
                )
              }
            : branch
        )
      })));
      
      setShowEditUserForm(false);
      setEditingUser(null);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error al actualizar el usuario';
      setError(errorMessage);
      console.error('Error updating user:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleUserStatus = async (brandId: string, branchId: string, userId: string) => {
    const user = brands
      .find(b => b.id === brandId)
      ?.branches.find(br => br.id === branchId)
      ?.users.find(u => u.id === userId);
    
    if (!user) return;

    try {
      const updatedUser = await updatePosUser(userId, {
        is_active: !user.is_active
      });
      
      setBrands(prev => prev.map(brand => 
      brand.id === brandId 
        ? {
            ...brand,
            branches: brand.branches.map(branch =>
              branch.id === branchId
                ? {
                    ...branch,
                      users: branch.users.map(u =>
                        u.id === userId
                          ? updatedUser
                          : u
                    )
                  }
                : branch
            )
          }
        : brand
    ));
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error al actualizar el estado del usuario';
      setError(errorMessage);
      console.error('Error toggling user status:', err);
    }
  };

  const addAdminPanelUser = async () => {
    if (!adminForm.name || !adminForm.email || !adminForm.password) {
      setError('Nombre, email y contraseña son obligatorios para crear administrador');
      return;
    }
    try {
      setIsSubmitting(true);
      setError(null);
      const created = await createAdminUser({
        name: adminForm.name,
        email: adminForm.email,
        password: adminForm.password,
        role: adminForm.role,
      });
      setAdminUsers((prev) => [created, ...prev]);
      setAdminForm({ name: '', email: '', password: '', role: 'admin' });
      setShowAdminForm(false);
      setShowAdminPassword(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al crear administrador');
    } finally {
      setIsSubmitting(false);
    }
  };

  const saveAdminPanelUser = async () => {
    if (!editingAdmin) return;
    try {
      setIsSubmitting(true);
      setError(null);
      const payload: { name?: string; role?: AdminUserRole; is_active?: boolean; password?: string } = {
        name: editingAdmin.name,
        role: editingAdmin.role,
        is_active: editingAdmin.is_active,
      };
      if (editingAdminPassword) {
        payload.password = editingAdminPassword;
      }
      const updated = await updateAdminUser(editingAdmin.id, payload);
      setAdminUsers((prev) => prev.map((item) => (item.id === updated.id ? updated : item)));
      setEditingAdmin(null);
      setShowEditAdminForm(false);
      setEditingAdminPassword('');
      setShowAdminPassword(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al actualizar administrador');
    } finally {
      setIsSubmitting(false);
    }
  };

  const deactivateAdminPanelUser = async (id: string) => {
    if (!window.confirm('¿Deseas desactivar este administrador?')) return;
    try {
      setError(null);
      await deleteAdminUser(id);
      await loadAdminUsers();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al desactivar administrador');
    }
  };

  const getRoleLabel = (role: string) => {
    const roleLabels: Record<string, string> = {
      cajero: 'Cajero',
      admin_caja: 'Administrador de Caja',
      gerente_sucursal: 'Gerente de Sucursal',
      admin_tienda: 'Administrador de Tienda',
      operador_cent: 'Operador CENT',
      administrador_caja: 'Administrador de Caja',
      administrador_tienda: 'Administrador de Tienda'
    };
    return roleLabels[role] || role;
  };

  const formatAddress = (branch: Branch) => {
    const parts = [
      branch.address_street,
      branch.address_number,
      branch.address_colony,
      branch.address_city,
      branch.address_state
    ].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : branch.address_city || 'Sin dirección';
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-[#3bbcc8]" />
        <span className="ml-3 text-secondary-600">Cargando marcas...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="card p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="h-10 w-10 bg-[#3bbcc8]/10 rounded-lg flex items-center justify-center">
              <Building2 className="h-5 w-5 text-[#3bbcc8]" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-secondary-900">Gestión de Organización</h2>
              <p className="text-sm text-secondary-500">Administra marcas, sucursales y usuarios</p>
            </div>
          </div>
          <button
            onClick={() => setShowBrandForm(true)}
            className="btn-primary flex items-center space-x-2"
          >
            <Plus className="h-4 w-4" />
            <span>Agregar Marca</span>
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="card p-4 bg-red-50 border-red-200">
          <div className="flex items-center space-x-2">
            <AlertCircle className="h-5 w-5 text-red-600" />
            <span className="text-sm font-medium text-red-800">{error}</span>
            <button
              onClick={() => setError(null)}
              className="ml-auto text-red-600 hover:text-red-800"
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* Admin Panel Users */}
      <div className="card p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-secondary-900">Administradores del Panel</h3>
            <p className="text-sm text-secondary-500">
              Gestión de usuarios para acceso al panel admin (`/admin/admin-users`)
            </p>
          </div>
          <button onClick={() => setShowAdminForm(true)} className="btn-primary flex items-center space-x-2">
            <Plus className="h-4 w-4" />
            <span>Agregar Admin</span>
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-secondary-200">
            <thead className="bg-secondary-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-secondary-500 uppercase">Nombre</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-secondary-500 uppercase">Email</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-secondary-500 uppercase">Rol</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-secondary-500 uppercase">Estado</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-secondary-500 uppercase">Acciones</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-secondary-200">
              {adminUsers.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-secondary-500">
                    No hay administradores registrados
                  </td>
                </tr>
              ) : (
                adminUsers.map((admin) => (
                  <tr key={admin.id}>
                    <td className="px-4 py-3 text-sm text-secondary-900">{admin.name}</td>
                    <td className="px-4 py-3 text-sm text-secondary-700">{admin.email}</td>
                    <td className="px-4 py-3 text-sm text-secondary-700">{admin.role}</td>
                    <td className="px-4 py-3">
                      <span
                        className={`text-xs px-2 py-1 rounded-full ${
                          admin.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                        }`}
                      >
                        {admin.is_active ? 'Activo' : 'Inactivo'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => {
                            setEditingAdmin(admin);
                            setEditingAdminPassword('');
                            setShowEditAdminForm(true);
                          }}
                          className="p-1 text-blue-500 hover:bg-blue-50 rounded"
                          title="Editar admin"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => deactivateAdminPanelUser(admin.id)}
                          className="p-1 text-red-500 hover:bg-red-50 rounded"
                          title="Desactivar admin"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Brands List */}
      <div className="space-y-4">
        {brands.length === 0 ? (
          <div className="card p-8 text-center">
            <Building2 className="h-12 w-12 text-secondary-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-secondary-900 mb-2">No hay marcas registradas</h3>
            <p className="text-secondary-600 mb-4">Comienza agregando tu primera marca para organizar tu negocio</p>
            <button
              onClick={() => setShowBrandForm(true)}
              className="btn-primary"
            >
              Agregar Primera Marca
            </button>
          </div>
        ) : (
          brands.map(brand => (
            <div key={brand.id} className="card">
              {/* Brand Header */}
              <div className="p-4 border-b border-secondary-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <button
                      onClick={() => toggleBrandExpansion(brand.id)}
                      className="p-1 hover:bg-secondary-100 rounded"
                      disabled={loadingBranches.has(brand.id)}
                    >
                      {loadingBranches.has(brand.id) ? (
                        <Loader2 className="h-4 w-4 animate-spin text-secondary-600" />
                      ) : expandedBrands.has(brand.id) ? (
                        <ChevronDown className="h-4 w-4 text-secondary-600" />
                      ) : (
                        <ChevronRight className="h-4 w-4 text-secondary-600" />
                      )}
                    </button>
                    <div className="h-8 w-8 bg-[#3bbcc8]/10 rounded-lg flex items-center justify-center">
                      <Tag className="h-4 w-4 text-[#3bbcc8]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-secondary-900">{brand.name}</h3>
                      <p className="text-sm text-secondary-500">Alias: {brand.alias}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-secondary-500">
                      {brand.branches.length} sucursal{brand.branches.length !== 1 ? 'es' : ''}
                    </span>
                    <button
                      onClick={() => {
                        setBranchForm({ ...branchForm, brandId: brand.id });
                        setShowBranchForm(true);
                      }}
                      className="btn-secondary flex items-center space-x-1"
                    >
                      <Plus className="h-3 w-3" />
                      <span>Sucursal</span>
                    </button>
                    <button
                      onClick={() => handleDeleteBrand(brand.id)}
                      className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                      disabled={loadingBrands.has(brand.id)}
                    >
                      {loadingBrands.has(brand.id) ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                      <Trash2 className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </div>
              </div>

              {/* Branches */}
              {expandedBrands.has(brand.id) && (
                <div className="p-4 space-y-3">
                  {brand.branches.length === 0 ? (
                    <div className="text-center py-4 text-secondary-500">
                      No hay sucursales registradas para esta marca
                    </div>
                  ) : (
                    brand.branches.map(branch => (
                      <div key={branch.id} className="border border-secondary-200 rounded-lg">
                        {/* Branch Header */}
                        <div className="p-3 bg-secondary-50">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              <button
                                onClick={() => toggleBranchExpansion(branch.id)}
                                className="p-1 hover:bg-secondary-200 rounded"
                              >
                                {expandedBranches.has(branch.id) ? (
                                  <ChevronDown className="h-3 w-3 text-secondary-600" />
                                ) : (
                                  <ChevronRight className="h-3 w-3 text-secondary-600" />
                                )}
                              </button>
                              <div className="h-6 w-6 bg-secondary-200 rounded flex items-center justify-center">
                                <MapPin className="h-3 w-3 text-secondary-600" />
                              </div>
                              <div>
                                <h4 className="font-medium text-secondary-900">{branch.name}</h4>
                                <p className="text-xs text-secondary-500">
                                  {branch.alias} • {formatAddress(branch)}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center space-x-2">
                              <span className="text-xs text-secondary-500">
                                {branch.users.length} usuario{branch.users.length !== 1 ? 's' : ''}
                              </span>
                              <button
                                onClick={() => {
                                  setUserForm({ ...userForm, branchId: branch.id });
                                  setShowUserForm(true);
                                }}
                                className="btn-secondary flex items-center space-x-1 text-xs"
                              >
                                <Plus className="h-3 w-3" />
                                <span>Usuario</span>
                              </button>
                              <button
                                onClick={() => handleDeleteBranch(brand.id, branch.id)}
                                className="p-1 text-red-500 hover:bg-red-50 rounded"
                                disabled={loadingBranches.has(branch.id)}
                              >
                                {loadingBranches.has(branch.id) ? (
                                  <Loader2 className="h-3 w-3 animate-spin" />
                                ) : (
                                <Trash2 className="h-3 w-3" />
                                )}
                              </button>
                            </div>
                          </div>
                        </div>

                        {/* Users */}
                        {expandedBranches.has(branch.id) && (
                          <div className="p-3 space-y-2">
                            {branch.users.length === 0 ? (
                              <div className="text-center py-2 text-secondary-500 text-sm">
                                No hay usuarios asignados a esta sucursal
                              </div>
                            ) : (
                              branch.users.map(user => (
                                <div key={user.id} className="flex items-center justify-between p-2 bg-white border border-secondary-100 rounded">
                                  <div className="flex items-center space-x-3">
                                    <div className={`h-6 w-6 rounded-full flex items-center justify-center ${
                                      user.is_active ? 'bg-green-100' : 'bg-red-100'
                                    }`}>
                                      <UserCheck className={`h-3 w-3 ${
                                        user.is_active ? 'text-green-600' : 'text-red-600'
                                      }`} />
                                    </div>
                                    <div>
                                      <p className="text-sm font-medium text-secondary-900">{user.full_name}</p>
                                      <p className="text-xs text-secondary-500">{user.email}</p>
                                    </div>
                                  </div>
                                  <div className="flex items-center space-x-2">
                                    <span className={`text-xs px-2 py-1 rounded-full ${
                                      user.is_active 
                                        ? 'bg-green-100 text-green-700' 
                                        : 'bg-red-100 text-red-700'
                                    }`}>
                                      {user.is_active ? 'Activo' : 'Inactivo'}
                                    </span>
                                    <span className="text-xs bg-[#3bbcc8]/10 text-[#3bbcc8] px-2 py-1 rounded-full">
                                      {getRoleLabel(user.role)}
                                    </span>
                                    <button
                                      onClick={() => toggleUserStatus(brand.id, branch.id, user.id)}
                                      className="p-1 text-secondary-500 hover:bg-secondary-50 rounded"
                                      title={user.is_active ? 'Desactivar usuario' : 'Activar usuario'}
                                    >
                                      {user.is_active ? (
                                        <ToggleRight className="h-3 w-3 text-green-600" />
                                      ) : (
                                        <ToggleLeft className="h-3 w-3 text-red-600" />
                                      )}
                                    </button>
                                    <button
                                      onClick={() => editUser(user)}
                                      className="p-1 text-blue-500 hover:bg-blue-50 rounded"
                                      title="Editar usuario"
                                    >
                                      <Edit className="h-3 w-3" />
                                    </button>
                                    <button
                                      onClick={() => handleDeleteUser(brand.id, branch.id, user.id)}
                                      className="p-1 text-red-500 hover:bg-red-50 rounded"
                                      title="Eliminar usuario"
                                    >
                                      <Trash2 className="h-3 w-3" />
                                    </button>
                                  </div>
                                </div>
                              ))
                            )}
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Admin Form Modal */}
      {showAdminForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-md modal-mobile">
            <h3 className="text-lg font-semibold text-secondary-900 mb-4">Agregar Administrador</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">Nombre *</label>
                <input
                  type="text"
                  className="input-field"
                  value={adminForm.name}
                  onChange={(e) => setAdminForm({ ...adminForm, name: e.target.value })}
                  placeholder="Nombre completo"
                  disabled={isSubmitting}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">Email *</label>
                <input
                  type="email"
                  className="input-field"
                  value={adminForm.email}
                  onChange={(e) => setAdminForm({ ...adminForm, email: e.target.value })}
                  placeholder="admin@email.com"
                  disabled={isSubmitting}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">Rol</label>
                <select
                  className="input-field"
                  value={adminForm.role}
                  onChange={(e) => setAdminForm({ ...adminForm, role: e.target.value as AdminUserRole })}
                  disabled={isSubmitting}
                >
                  <option value="admin">admin</option>
                  <option value="super_admin">super_admin</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">Contraseña *</label>
                <div className="relative">
                  <input
                    type={showAdminPassword ? 'text' : 'password'}
                    className="input-field pr-10"
                    value={adminForm.password}
                    onChange={(e) => setAdminForm({ ...adminForm, password: e.target.value })}
                    placeholder="Contraseña"
                    disabled={isSubmitting}
                  />
                  <button
                    type="button"
                    onClick={() => setShowAdminPassword(!showAdminPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-secondary-400 hover:text-secondary-600"
                  >
                    {showAdminPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
            </div>
            <div className="flex space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowAdminForm(false);
                  setAdminForm({ name: '', email: '', password: '', role: 'admin' });
                  setShowAdminPassword(false);
                }}
                className="btn-secondary flex-1"
                disabled={isSubmitting}
              >
                Cancelar
              </button>
              <button onClick={addAdminPanelUser} className="btn-primary flex-1" disabled={isSubmitting}>
                {isSubmitting ? 'Guardando...' : 'Crear Admin'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Admin Form Modal */}
      {showEditAdminForm && editingAdmin && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-md modal-mobile">
            <h3 className="text-lg font-semibold text-secondary-900 mb-4">Editar Administrador</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">Nombre</label>
                <input
                  type="text"
                  className="input-field"
                  value={editingAdmin.name}
                  onChange={(e) => setEditingAdmin({ ...editingAdmin, name: e.target.value })}
                  disabled={isSubmitting}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">Rol</label>
                <select
                  className="input-field"
                  value={editingAdmin.role}
                  onChange={(e) =>
                    setEditingAdmin({ ...editingAdmin, role: e.target.value as AdminUserRole })
                  }
                  disabled={isSubmitting}
                >
                  <option value="admin">admin</option>
                  <option value="super_admin">super_admin</option>
                </select>
              </div>
              <div>
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={editingAdmin.is_active}
                    onChange={(e) => setEditingAdmin({ ...editingAdmin, is_active: e.target.checked })}
                    className="rounded border-secondary-300 text-[#3bbcc8] focus:ring-[#3bbcc8]"
                    disabled={isSubmitting}
                  />
                  <span className="text-sm font-medium text-secondary-700">Usuario activo</span>
                </label>
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">
                  Nueva contraseña (opcional)
                </label>
                <input
                  type="password"
                  className="input-field"
                  value={editingAdminPassword}
                  onChange={(e) => setEditingAdminPassword(e.target.value)}
                  placeholder="Dejar vacío para no cambiar"
                  disabled={isSubmitting}
                />
              </div>
            </div>
            <div className="flex space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowEditAdminForm(false);
                  setEditingAdmin(null);
                  setEditingAdminPassword('');
                }}
                className="btn-secondary flex-1"
                disabled={isSubmitting}
              >
                Cancelar
              </button>
              <button onClick={saveAdminPanelUser} className="btn-primary flex-1" disabled={isSubmitting}>
                {isSubmitting ? 'Guardando...' : 'Actualizar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Brand Form Modal */}
      {showBrandForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-md modal-mobile">
            <h3 className="text-lg font-semibold text-secondary-900 mb-4">Agregar Nueva Marca</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">Nombre de la Marca</label>
                <input
                  type="text"
                  className="input-field"
                  value={brandForm.name}
                  onChange={(e) => setBrandForm({ ...brandForm, name: e.target.value })}
                  placeholder="Ej: Walmart"
                  disabled={isSubmitting}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">Alias</label>
                <input
                  type="text"
                  className="input-field"
                  value={brandForm.alias}
                  onChange={(e) => setBrandForm({ ...brandForm, alias: e.target.value })}
                  placeholder="Ej: WMT"
                  disabled={isSubmitting}
                />
              </div>
            </div>
            <div className="flex space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowBrandForm(false);
                  setBrandForm({ name: '', alias: '' });
                  setError(null);
                }}
                className="btn-secondary flex-1"
                disabled={isSubmitting}
              >
                Cancelar
              </button>
              <button
                onClick={addBrand}
                className="btn-primary flex-1"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin inline mr-2" />
                    Guardando...
                  </>
                ) : (
                  'Agregar Marca'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Branch Form Modal */}
      {showBranchForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-md modal-mobile max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-secondary-900 mb-4">Agregar Nueva Sucursal</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">Nombre de la Sucursal *</label>
                <input
                  type="text"
                  className="input-field"
                  value={branchForm.name}
                  onChange={(e) => setBranchForm({ ...branchForm, name: e.target.value })}
                  placeholder="Ej: Sucursal Centro"
                  disabled={isSubmitting}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">Alias *</label>
                <input
                  type="text"
                  className="input-field"
                  value={branchForm.alias}
                  onChange={(e) => setBranchForm({ ...branchForm, alias: e.target.value })}
                  placeholder="Ej: CENTRO"
                  disabled={isSubmitting}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">Calle</label>
                <input
                  type="text"
                  className="input-field"
                  value={branchForm.address_street}
                  onChange={(e) => setBranchForm({ ...branchForm, address_street: e.target.value })}
                  placeholder="Ej: Av. Principal"
                  disabled={isSubmitting}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">Ciudad</label>
                <input
                  type="text"
                  className="input-field"
                  value={branchForm.address_city}
                  onChange={(e) => setBranchForm({ ...branchForm, address_city: e.target.value })}
                  placeholder="Ej: Ciudad de México"
                  disabled={isSubmitting}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">Estado</label>
                <input
                  type="text"
                  className="input-field"
                  value={branchForm.address_state}
                  onChange={(e) => setBranchForm({ ...branchForm, address_state: e.target.value })}
                  placeholder="Ej: CDMX"
                  disabled={isSubmitting}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">País</label>
                <input
                  type="text"
                  className="input-field"
                  value={branchForm.address_country}
                  onChange={(e) => setBranchForm({ ...branchForm, address_country: e.target.value })}
                  placeholder="Ej: México"
                  disabled={isSubmitting}
                />
              </div>
            </div>
            <div className="flex space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowBranchForm(false);
                  setBranchForm({ 
                    name: '', 
                    alias: '', 
                    address_street: '', 
                    address_city: '',
                    address_state: '',
                    address_country: 'México',
                    brandId: '' 
                  });
                  setError(null);
                }}
                className="btn-secondary flex-1"
                disabled={isSubmitting}
              >
                Cancelar
              </button>
              <button
                onClick={addBranch}
                className="btn-primary flex-1"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin inline mr-2" />
                    Guardando...
                  </>
                ) : (
                  'Agregar Sucursal'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* User Form Modal */}
      {showUserForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-md modal-mobile">
            <h3 className="text-lg font-semibold text-secondary-900 mb-4">Agregar Nuevo Usuario</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">Nombre Completo *</label>
                <input
                  type="text"
                  className="input-field"
                  value={userForm.full_name}
                  onChange={(e) => setUserForm({ ...userForm, full_name: e.target.value })}
                  placeholder="Ej: Juan Pérez"
                  disabled={isSubmitting}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">Email *</label>
                <input
                  type="email"
                  className="input-field"
                  value={userForm.email}
                  onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                  placeholder="Ej: juan@empresa.com"
                  disabled={isSubmitting}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">Contraseña *</label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    className="input-field pr-10"
                    value={userForm.password}
                    onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                    placeholder="Ingresa una contraseña segura"
                    disabled={isSubmitting}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-secondary-400 hover:text-secondary-600"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">Rol *</label>
                <select
                  className="input-field"
                  value={userForm.role}
                  onChange={(e) => setUserForm({ ...userForm, role: e.target.value as any })}
                  disabled={isSubmitting}
                >
                  <option value="cajero">Cajero</option>
                  <option value="admin_caja">Administrador de Caja</option>
                  <option value="gerente_sucursal">Gerente de Sucursal</option>
                  <option value="admin_tienda">Administrador de Tienda</option>
                  <option value="operador_cent">Operador CENT</option>
                </select>
              </div>
            </div>
            <div className="flex space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowUserForm(false);
                  setUserForm({ 
                    full_name: '', 
                    email: '', 
                    password: '', 
                    role: 'cajero', 
                    branchId: '' 
                  });
                  setError(null);
                }}
                className="btn-secondary flex-1"
                disabled={isSubmitting}
              >
                Cancelar
              </button>
              <button
                onClick={addUser}
                className="btn-primary flex-1"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin inline mr-2" />
                    Guardando...
                  </>
                ) : (
                  'Agregar Usuario'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Form Modal */}
      {showEditUserForm && editingUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-md modal-mobile">
            <h3 className="text-lg font-semibold text-secondary-900 mb-4">Editar Usuario</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">Nombre Completo</label>
                <input
                  type="text"
                  className="input-field"
                  value={editingUser.full_name}
                  onChange={(e) => setEditingUser({ ...editingUser, full_name: e.target.value })}
                  placeholder="Ej: Juan Pérez"
                  disabled={isSubmitting}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">Email</label>
                <input
                  type="email"
                  className="input-field"
                  value={editingUser.email}
                  onChange={(e) => setEditingUser({ ...editingUser, email: e.target.value })}
                  placeholder="Ej: juan@empresa.com"
                  disabled={isSubmitting}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">Rol</label>
                <select
                  className="input-field"
                  value={editingUser.role}
                  onChange={(e) => setEditingUser({ ...editingUser, role: e.target.value as any })}
                  disabled={isSubmitting}
                >
                  <option value="cajero">Cajero</option>
                  <option value="admin_caja">Administrador de Caja</option>
                  <option value="gerente_sucursal">Gerente de Sucursal</option>
                  <option value="admin_tienda">Administrador de Tienda</option>
                  <option value="operador_cent">Operador CENT</option>
                </select>
              </div>
              <div>
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={editingUser.is_active}
                    onChange={(e) => setEditingUser({ ...editingUser, is_active: e.target.checked })}
                    className="rounded border-secondary-300 text-[#3bbcc8] focus:ring-[#3bbcc8]"
                    disabled={isSubmitting}
                  />
                  <span className="text-sm font-medium text-secondary-700">Usuario activo</span>
                </label>
              </div>
            </div>
            <div className="flex space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowEditUserForm(false);
                  setEditingUser(null);
                  setError(null);
                }}
                className="btn-secondary flex-1"
                disabled={isSubmitting}
              >
                Cancelar
              </button>
              <button
                onClick={updateUser}
                className="btn-primary flex-1"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin inline mr-2" />
                    Guardando...
                  </>
                ) : (
                  'Actualizar Usuario'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GestionSection;
